/**
 * SEO Variable Replacement
 *
 * Replaces %%VARIABLE%% placeholders in text with actual values.
 * Used in SEO titles, descriptions, and other CMS content.
 *
 * Based on breitling-storefront-next/src/lib/seo/SeoVariables.ts
 *
 * ## Supported Variables
 *
 * ### Collection/Category
 * - %%COLLECTION_NAME%% - Watch collection name (e.g., "Navitimer")
 * - %%STRAP_MATERIAL%% - Strap material (e.g., "Leather")
 *
 * ### Product Details
 * - %%PRODUCT_NAME%% - Product/model name
 * - %%REFERENCE_NUMBER%% - Product SKU/reference
 * - %%DIAL_COLOR%% - Watch dial color
 * - %%CASE_MATERIAL%% - Watch case material
 * - %%LUG_SIZE%% - Watch lug width
 * - %%MARKETING_DESCRIPTION%% - Product marketing description
 *
 * ### Eyewear
 * - %%SUNGLASSES_NAME%% - Sunglasses product name
 * - %%FRAME_COLOR%% - Frame color
 * - %%FRAME_SHAPE%% - Frame shape
 * - %%LENS_COLOR%% - Lens color
 *
 * ### Location
 * - %%BOUTIQUE_NAME%% - Store/boutique name
 * - %%COUNTRY%% - Country name
 * - %%BOUTIQUE_LIST_H1%% - Boutique list heading
 *
 * ### URL Placeholders (handled separately by ensureLocale)
 * - %%COUNTRY%% - Country code slug
 * - %%LANGUAGE%% - Language code slug
 *
 * @example
 * ```ts
 * import { replaceSeoVariables, type SeoVariables } from '@/lib/seo/seoVariables';
 *
 * const title = "%%COLLECTION_NAME%% Watches | Gallet";
 * const variables: SeoVariables = { collectionName: 'Navitimer' };
 * const result = replaceSeoVariables(title, variables);
 * // => "Navitimer Watches | Gallet"
 * ```
 */

import { capitalize } from '@/lib/utils/string';

// ============================================
// Types
// ============================================

/**
 * All available SEO variable values.
 * Undefined values will leave the placeholder in place.
 */
export interface SeoVariables {
  // Collection/Category
  collectionName?: string | null;
  strapMaterial?: string | null;

  // Product Details
  productName?: string | null;
  referenceNumber?: string | null;
  dialColor?: string | null;
  caseMaterial?: string | null;
  lugSize?: string | null;
  marketingDescription?: string | null;

  // Eyewear
  sunglassesName?: string | null;
  frameColor?: string | null;
  frameShape?: string | null;
  lensColor?: string | null;

  // Location
  boutiqueName?: string | null;
  countryName?: string | null;
  boutiqueListH1?: string | null;

  /**
   * Router locale (e.g. "us-en", "de-de", "ch-de").
   * Used for language-specific casing per SEO-RULES.md §1:
   * German (de) keeps original capitalization on material/color variables;
   * all other languages lowercase them.
   */
  locale?: string | null;
}

// ============================================
// Main Function
// ============================================

/**
 * Replace all %%VARIABLE%% placeholders in text with actual values.
 *
 * If a variable is not provided (undefined or null), the placeholder is left as-is.
 * This allows partial replacement when not all values are available.
 *
 * @param text - Text containing %%VARIABLE%% placeholders
 * @param variables - Object containing variable values
 * @returns Text with placeholders replaced
 */
export function replaceSeoVariables(
  text: string | null | undefined,
  variables: SeoVariables
): string {
  if (!text) return '';

  const {
    collectionName,
    strapMaterial,
    productName,
    referenceNumber,
    dialColor,
    caseMaterial,
    lugSize,
    marketingDescription,
    boutiqueName,
    countryName,
    boutiqueListH1,
    sunglassesName,
    frameColor,
    frameShape,
    lensColor,
    locale,
  } = variables;

  const lc = shouldLowercaseMaterialColor(locale);

  let result = text;

  // Collection/Category
  result = replaceVariable(result, 'COLLECTION_NAME', collectionName);
  result = replaceVariable(
    result,
    'STRAP_MATERIAL',
    lc ? strapMaterial?.toLowerCase() : strapMaterial
  );

  // Product Details
  result = replaceVariable(
    result,
    'PRODUCT_NAME',
    productName ? formatProductName(productName) : null
  );
  result = replaceVariable(result, 'REFERENCE_NUMBER', referenceNumber);
  result = replaceVariable(result, 'DIAL_COLOR', lc ? dialColor?.toLowerCase() : dialColor);
  result = replaceVariable(
    result,
    'CASE_MATERIAL',
    lc ? caseMaterial?.toLowerCase() : caseMaterial
  );
  result = replaceVariable(result, 'LUG_SIZE', lugSize);
  result = replaceVariable(result, 'MARKETING_DESCRIPTION', marketingDescription);

  // Location
  result = replaceVariable(result, 'BOUTIQUE_NAME', boutiqueName);
  result = replaceVariable(result, 'COUNTRY', countryName);
  result = replaceVariable(result, 'BOUTIQUE_LIST_H1', boutiqueListH1);

  // Eyewear
  result = replaceVariable(result, 'SUNGLASSES_NAME', sunglassesName || productName);
  result = replaceVariable(result, 'FRAME_COLOR', lc ? frameColor?.toLowerCase() : frameColor);
  result = replaceVariable(result, 'FRAME_SHAPE', frameShape);
  result = replaceVariable(result, 'LENS_COLOR', lc ? lensColor?.toLowerCase() : lensColor);

  // Capitalize first letter of result (if changed)
  if (result !== text) {
    result = capitalizeFirstLetter(result);
  }

  return result;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Per SEO-RULES.md §1 — German keeps original capitalization on material/color
 * variables; all other languages lowercase them.
 *
 * Returns true when lowercasing should be applied (i.e. language is NOT German).
 * When no locale is provided, defaults to false (preserve casing) for safety.
 */
function shouldLowercaseMaterialColor(locale: string | null | undefined): boolean {
  if (!locale) return false;
  const language = locale.split('-')[1];
  return language !== 'de';
}

/**
 * Replace a single variable in text.
 * If value is null/undefined, keeps the placeholder.
 */
function replaceVariable(text: string, varName: string, value: string | null | undefined): string {
  const placeholder = `%%${varName}%%`;

  if (value == null) {
    // Keep the placeholder if no value provided
    return text;
  }

  return text.replaceAll(placeholder, value);
}

/**
 * Format product name: convert hyphens to spaces and title case.
 */
function formatProductName(name: string): string {
  return name
    .replaceAll('-', ' ')
    .split(' ')
    .map((word) => capitalize(word))
    .join(' ');
}

/**
 * Capitalize first letter of a string.
 */
function capitalizeFirstLetter(text: string): string {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// ============================================
// URL Placeholder Replacement
// ============================================

/**
 * Replace URL placeholders with locale values.
 *
 * Used for URLs that contain %%COUNTRY%% and %%LANGUAGE%% placeholders.
 *
 * @param url - URL containing placeholders
 * @param countrySlug - Country code (e.g., "US")
 * @param languageSlug - Language code (e.g., "en")
 * @returns URL with placeholders replaced
 */
export function replaceUrlPlaceholders(
  url: string,
  countrySlug: string,
  languageSlug: string
): string {
  return url
    .replaceAll('%%COUNTRY%%', countrySlug.toUpperCase())
    .replaceAll('%%LANGUAGE%%', languageSlug.toLowerCase());
}
